import React, { useState, useEffect, useRef } from 'react';

/**
 * Smooth typewriter with natural rhythm.
 * Uses a single mutable ref for state so React never batches or
 * cancels the timeout chain — no cursor flicker, no abrupt jumps.
 */
const Typewriter = ({
  phrases,
  typeSpeed = 58,
  deleteSpeed = 30,
  pauseMs = 2200,
  deletePauseMs = 480,
  className = '',
  wrapper: Tag = 'span',
}) => {
  const [text, setText] = useState('');
  const s = useRef({ pi: 0, ci: 0, del: false });
  const phRef = useRef(phrases);
  const timingRef = useRef({ typeSpeed, deleteSpeed, pauseMs, deletePauseMs });
  useEffect(() => { phRef.current = phrases; }, [phrases]);
  useEffect(() => {
    timingRef.current = { typeSpeed, deleteSpeed, pauseMs, deletePauseMs };
  }, [typeSpeed, deleteSpeed, pauseMs, deletePauseMs]);

  useEffect(() => {
    let timer;
    const tick = () => {
      const phrase = phRef.current[s.current.pi];
      const { ci, del } = s.current;
      const timing = timingRef.current;

      if (!del) {
        if (ci < phrase.length) {
          s.current.ci += 1;
          setText(phrase.slice(0, s.current.ci));
          // Slight human-feel jitter: pause longer on punctuation
          const ch = phrase[s.current.ci - 1];
          const extra = /[,.]/.test(ch) ? 180 : /[ ]/.test(ch) ? 20 : 0;
          timer = setTimeout(tick, timing.typeSpeed + Math.random() * 22 + extra);
        } else {
          timer = setTimeout(() => { s.current.del = true; tick(); }, timing.pauseMs);
        }
      } else {
        if (ci > 0) {
          s.current.ci -= 1;
          setText(phrase.slice(0, s.current.ci));
          timer = setTimeout(tick, timing.deleteSpeed + Math.random() * 10);
        } else {
          s.current.del = false;
          s.current.pi = (s.current.pi + 1) % phRef.current.length;
          timer = setTimeout(tick, timing.deletePauseMs);
        }
      }
    };

    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, []); // runs once — state lives entirely in the ref

  return (
    <Tag className={className}>
      {text}
      <span className="tw-cursor" aria-hidden="true" />
    </Tag>
  );
};

export default Typewriter;
